import { Button, Snackbar, Alert, IconButton } from "@mui/material";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { PubSub } from "aws-amplify";

const encodeBase64 = (str) => btoa(unescape(encodeURIComponent(str)));
const decodeBase64 = (b64) => decodeURIComponent(escape(atob(b64)));

const ProductionTerminal = (props) => {
  const terminalRef = useRef(null);
  const terminalInstance = useRef(null);
  const outputSubscriptionRef = useRef(null);
  const fitAddonRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const isConnectedRef = useRef(false);

  const packetBufferRef = useRef(new Map());
  const expectedSeqRef = useRef(0);
  const writeQueueRef = useRef([]);
  const isWritingRef = useRef(false);
  const reorderTimeoutRef = useRef(null);

  const inputBufferRef = useRef("");

  const { deviceId = "drone-001" } = props|| {};

  const sessionId = useRef(crypto.randomUUID()).current;

  const publishTopic = `drone/${deviceId}/terminal/${sessionId}/input`;
  const subscribeTopic = `drone/${deviceId}/terminal/${sessionId}/output`;
  const resizeTopic = `drone/${deviceId}/terminal/${sessionId}/resize`;

  const [isConnected, setIsConnected] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = useCallback((message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const processWriteQueue = useCallback(() => {
    if (isWritingRef.current || writeQueueRef.current.length === 0) {
      return;
    }

    isWritingRef.current = true;
    const terminal = terminalInstance.current;
    const items = writeQueueRef.current.splice(0);

    requestAnimationFrame(() => {
      try {
        for (const data of items) {
          if (terminal) {
            terminal.write(data);
          }
        }
      } catch (err) {
      } finally {
        isWritingRef.current = false;
        if (writeQueueRef.current.length > 0) {
          processWriteQueue();
        }
      }
    });
  }, []);

  const queueWrite = useCallback((data) => {
    writeQueueRef.current.push(data);
    processWriteQueue();
  }, [processWriteQueue]);

  const processBufferedPackets = useCallback(() => {
    const buffer = packetBufferRef.current;
    let expectedSeq = expectedSeqRef.current;

    let packetsProcessed = [];
    while (buffer.has(expectedSeq)) {
      const data = buffer.get(expectedSeq);
      buffer.delete(expectedSeq);
      packetsProcessed.push(data);
      expectedSeq++;
    }

    if (packetsProcessed.length > 0) {
      const combinedData = packetsProcessed.join('');
      queueWrite(combinedData);
    }

    expectedSeqRef.current = expectedSeq;

    if (buffer.size > 0) {
      clearTimeout(reorderTimeoutRef.current);
      reorderTimeoutRef.current = setTimeout(() => {
        const sortedSeqs = Array.from(buffer.keys()).sort((a, b) => a - b);

        if (sortedSeqs.length > 0) {
          console.warn(` Packet gap detected. Expected ${expectedSeqRef.current}, got ${sortedSeqs[0]}`);

          const remainingPackets = [];
          for (const seq of sortedSeqs) {
            const data = buffer.get(seq);
            remainingPackets.push(data);
            buffer.delete(seq);
          }

          if (remainingPackets.length > 0) {
            queueWrite(remainingPackets.join(''));
          }

          expectedSeqRef.current = sortedSeqs[sortedSeqs.length - 1] + 1;
        }
      }, 250);
    }
  }, [queueWrite]);

  const sendResize = useCallback((cols, rows) => {
    if (isConnectedRef.current) {
      PubSub.publish(resizeTopic, {
        cols,
        rows,
      }).catch((err) => {
        console.error("Resize publish error:", err);
      });
    }
  }, [resizeTopic]);

  const clearTerminal = useCallback((arg) => {
    if (terminalInstance.current && isConnectedRef.current) {
      try {
        terminalInstance.current.clear();
        terminalInstance.current.clearSelection();

        expectedSeqRef.current = 0;
        packetBufferRef.current.clear();
        writeQueueRef.current = [];
        isWritingRef.current = false;
        inputBufferRef.current = "";

        if (reorderTimeoutRef.current) {
          clearTimeout(reorderTimeoutRef.current);
          reorderTimeoutRef.current = null;
        }

        PubSub.publish(publishTopic, {
          type: "stdin",
          b64: encodeBase64(String(arg)),
        }, {
          mqtt: { qos: 1 }
        }).catch((err) => {
          console.error("Clear command publish error:", err);
        });

        if (fitAddonRef.current) {
          setTimeout(() => {
            try {
              fitAddonRef.current.fit();
              const dims = fitAddonRef.current.proposeDimensions();
              if (dims && terminalInstance.current) {
                terminalInstance.current.resize(dims.cols, dims.rows);
              }
            } catch (e) {
              console.error("Error refitting after clear:", e);
            }
          }, 50);
        }
      } catch (error) {
        console.error("Error clearing terminal:", error);
      }
    }
  }, [publishTopic]);

  useEffect(() => {
    const styleId = "terminal-custom-styles";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      .xterm,
      .xterm *,
      .xterm-screen,
      .xterm-rows,
      .xterm-viewport {
        text-align: left !important;
        direction: ltr !important;
      }

      .xterm {
        padding: 0 !important;
        height: 100% !important;
        width: 100% !important;
        position: relative !important;
      }

      .xterm-viewport {
        background-color: #0c0c0c !important;
        overflow-y: auto !important;
        scrollbar-width: thin;
        scrollbar-color: rgba(121, 121, 121, 0.4) transparent;
                padding-bottom: 10px !important; 

      }

      .xterm-viewport::-webkit-scrollbar {
        width: 10px;
      }

      .xterm-viewport::-webkit-scrollbar-track {
        background: transparent;
      }

      .xterm-viewport::-webkit-scrollbar-thumb {
        background-color: rgba(121, 121, 121, 0.4);
        border-radius: 10px;
        border: 2px solid transparent;
      }

      .xterm-viewport::-webkit-scrollbar-thumb:hover {
        background-color: rgba(100, 100, 100, 0.7);
      }

      .xterm-screen {
        display: block !important;
        text-align: left !important;
      }

      .xterm-rows {
        text-align: left !important;
        position: relative !important;
      }

      .xterm-cursor-layer,
      .xterm-selection-layer {
        position: absolute !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    // Captured for the cleanup below: the rule wants the ref read at setup, and
    // this ref is assigned once (useRef(new Map())) and only ever mutated.
    const packetBuffer = packetBufferRef.current;
    const terminal = new Terminal({
      cursorBlink: true,
      cursorStyle: "block",
      fontSize: 14,
      fontFamily: "'Cascadia Code', Consolas, 'Courier New', monospace",
      fontWeight: 400,
      fontWeightBold: 700,
      lineHeight: 1.0,
      letterSpacing: 0,
      scrollback: 20000,
       padding: {
        top: 10,
        bottom: 50, 
        left: 10,
        right: 10
      },
      theme: {
        background: "#0c0c0c",
        foreground: "#cccccc",
        cursor: "#ffffff",
        cursorAccent: "#0c0c0c",
        selectionBackground: "rgba(255, 255, 255, 0.3)",
        selectionForeground: undefined,
        black: "#0c0c0c",
        red: "#c50f1f",
        green: "#13a10e",
        yellow: "#c19c00",
        blue: "#0037da",
        magenta: "#881798",
        cyan: "#3a96dd",
        white: "#cccccc",
        brightBlack: "#767676",
        brightRed: "#e74856",
        brightGreen: "#16c60c",
        brightYellow: "#f9f1a5",
        brightBlue: "#3b78ff",
        brightMagenta: "#b4009e",
        brightCyan: "#61d6d6",
        brightWhite: "#f2f2f2",
      },
      allowProposedApi: true,
      allowTransparency: false,
      convertEol: true,
      disableStdin: false,
      windowsMode: true,
      macOptionIsMeta: false,
      fastScrollModifier: "shift",
      scrollOnUserInput: true,
      bellStyle: "none",
      rendererType: "canvas",
      drawBoldTextInBrightColors: true,
      windowOptions: {
        setWinLines: false,
      },
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    fitAddonRef.current = fitAddon;

    if (terminalRef.current) {
      terminal.open(terminalRef.current);

      const fitTerminal = () => {
        try {
          fitAddon.fit();
          const dims = fitAddon.proposeDimensions();
          if (dims) {
            terminal.resize(dims.cols, dims.rows);
            sendResize(dims.cols, dims.rows);
          }
        } catch (e) {
          console.error("Error fitting terminalss:", e);
        }
      };

      setTimeout(fitTerminal, 0);
      setTimeout(fitTerminal, 50);
      setTimeout(fitTerminal, 150);
      setTimeout(fitTerminal, 300);
    }

    terminal.focus();
    terminalInstance.current = terminal;

    outputSubscriptionRef.current = PubSub.subscribe(subscribeTopic, {
      mqtt: { qos: 1 }
    }).subscribe({
      next: ({ value }) => {
        if (value?.type === "exit") {
          setIsConnected(false);
          isConnectedRef.current = false;
          showSnackbar("Terminal session ended", "warning");
          queueWrite("\r\n\x1b[1;31m[Terminal session ended]\x1b[0m\r\n");
        } else if (value?.b64 && value?.seq !== undefined) {
          try {
            const data = decodeBase64(value.b64);
            const seq = value.seq;

            packetBufferRef.current.set(seq, data);
            processBufferedPackets();
          } catch (err) {
            console.error("Error decoding terminal data:", err);
          }
        }
      },
      error: (err) => {
        console.error("Subscription error:", err);
        setIsConnected(false);
        isConnectedRef.current = false;
        showSnackbar("Disconnected from drone", "error");
      },
    });
    const statusTopic = `drone/${deviceId}/status`; 

    const statusSub = PubSub.subscribe(statusTopic, {
      mqtt: { qos: 1 } 
    }).subscribe({
      next: ({ value }) => {
        

        if (value === "Offline" || value?.status === "Offline") {
           setIsConnected(false);
           isConnectedRef.current = false;
           showSnackbar("Device is Offline", "error");
        } 
        else if (value === "Online" || value?.status === "Online") {
           setIsConnected(true);
           isConnectedRef.current = true;
        }
      },
    });

    setIsConnected(true);
    isConnectedRef.current = true;
    showSnackbar("Connected to drone terminal", "success");

    const dataDisposable = terminal.onData((data) => {
      if (isConnectedRef.current) {


        PubSub.publish(publishTopic, {
          type: "stdin",
          b64: encodeBase64(data),
        }, {
          mqtt: { qos: 1 }
        }).catch((err) => {
        });
      }
    });

    const handleResize = () => {
      clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => {
        if (fitAddonRef.current && terminalInstance.current) {
          try {
            fitAddonRef.current.fit();
            const dims = fitAddonRef.current.proposeDimensions();
            if (dims) {
              terminalInstance.current.resize(dims.cols, dims.rows);
              sendResize(dims.cols, dims.rows);
            }
          } catch (e) {
          }
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeoutRef.current);
      clearTimeout(reorderTimeoutRef.current);
      dataDisposable.dispose();
      statusSub.unsubscribe();
      outputSubscriptionRef.current?.unsubscribe();
      writeQueueRef.current = [];
      isWritingRef.current = false;
      packetBuffer.clear();
      inputBufferRef.current = "";
      terminal.dispose();
    };
  }, [publishTopic, subscribeTopic, resizeTopic, deviceId, sendResize, showSnackbar, queueWrite, processBufferedPackets, ]);

  const sendCommand = useCallback((command) => {
    if (isConnected && terminalInstance.current) {
      if (command.trim().toLowerCase() === "cls") {
        clearTerminal();
        return;
      }

      PubSub.publish(publishTopic, {
        type: "stdin",
        b64: encodeBase64(command + "\r"),
      }, {
        mqtt: { qos: 1 }
      }).catch((err) => {
      });
    }
  }, [isConnected, publishTopic, clearTerminal]);

  const sendText = (text) => {
    if (isConnected && terminalInstance.current) {
      PubSub.publish(publishTopic, {
        type: "stdin",
        b64: encodeBase64(text),
      }, {
        mqtt: { qos: 1 }
      }).catch((err) => {
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100vh",
        backgroundColor: "#0c0c0c",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#252526",
          borderBottom: "1px solid #3e3e42",
          padding: "8px 16px",
          height: "35px",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                fontSize: "13px",
                color: "#cccccc",
                fontWeight: "500",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              Terminal — {deviceId}
            </span>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: isConnected ? "#4ec9b0" : "#f48771",
                boxShadow: isConnected
                  ? "0 0 4px rgba(78, 201, 176, 0.6)"
                  : "0 0 4px rgba(244, 135, 113, 0.6)",
              }}
            />
          </div>
          <span
            style={{
              fontSize: "11px",
              color: "#858585",
              fontFamily: "system-ui, -apple-system, sans-serif",
            }}
          >
            {isConnected ? "Connected" : "Disconnected"}
          </span>
        </div>

        <div style={{ display: "flex", gap: "4px" }}>
          <IconButton
            size="small"
            onClick={() => clearTerminal("cls")}
            title="Clear Terminal"
            sx={{
              color: "#a63f3fff",
              padding: "4px",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm3.854 9.854a.5.5 0 0 1-.708 0L8 7.707l-3.146 3.147a.5.5 0 0 1-.708-.708L7.293 7 4.146 3.854a.5.5 0 1 1 .708-.708L8 6.293l3.146-3.147a.5.5 0 0 1 .708.708L8.707 7l3.147 3.146a.5.5 0 0 1 0 .708z" />
            </svg>
          </IconButton>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          backgroundColor: "#0c0c0c",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          ref={terminalRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />
      </div>

      <div
        style={{
          backgroundColor: "#252526",
          borderTop: "1px solid #3e3e42",
          padding: "8px 16px",
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          minHeight: "45px",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Button
          onClick={() => sendCommand("dir")}
          disabled={!isConnected}
          size="small"
          sx={{
            color: "#cccccc",
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
            minHeight: "28px",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            "&:disabled": { color: "#6e6e6e", backgroundColor: "transparent" },
          }}
        >
          dir
        </Button>
        <Button
          onClick={() => sendCommand("cd")}
          disabled={!isConnected}
          size="small"
          sx={{
            color: "#cccccc",
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
            minHeight: "28px",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            "&:disabled": { color: "#6e6e6e", backgroundColor: "transparent" },
          }}
        >
          cd
        </Button>
        <Button
          onClick={() => sendCommand("ipconfig")}
          disabled={!isConnected}
          size="small"
          sx={{
            color: "#cb1919ff",
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
            minHeight: "28px",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            "&:disabled": { color: "#6e6e6e", backgroundColor: "transparent" },
          }}
        >
          ipconfig
        </Button>
        <Button
          onClick={() => sendText("\x03")}
          disabled={!isConnected}
          size="small"
          sx={{
            color: "#cccccc",
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
            minHeight: "28px",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            "&:disabled": { color: "#6e6e6e", backgroundColor: "transparent" },
          }}
        >
          Ctrl+C
        </Button>
        <Button
          onClick={() => sendCommand("cls")}
          disabled={!isConnected}
          size="small"
          sx={{
            color: "#cccccc",
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
            minHeight: "28px",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            "&:disabled": { color: "#6e6e6e", backgroundColor: "transparent" },
          }}
        >
          cls
        </Button>
        <Button
          onClick={() => sendCommand("shutdown /r /t 0")}
          disabled={!isConnected}
          size="small"
          sx={{
            color: "#f48771",
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
            minHeight: "28px",
            backgroundColor: "rgba(244, 135, 113, 0.1)",
            "&:hover": { backgroundColor: "rgba(244, 135, 113, 0.2)" },
            "&:disabled": { color: "#6e6e6e", backgroundColor: "transparent" },
          }}
        >
          reboot
        </Button>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ bottom: "60px !important" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            fontSize: "13px",
            "& .MuiAlert-icon": { fontSize: "18px" },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProductionTerminal;